<template>
  <div class="bg-white shadow-sm border border-slate-200 overflow-hidden">
    <div class="px-6 pt-2 pb-1">
      <div class="flex items-baseline gap-2 flex-wrap">
        <h2 class="text-base font-bold text-[#0d4169] text-left">
          {{ resolvedCompanyName }}
        </h2>
        <span class="text-sm font-bold text-slate-600">Payroll Report</span>
      </div>
    </div>

    <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>
    <MonthlyConfigurationFilters :show-search-results-table="false" :api-base="apiBase" />

    <div class="px-2 pb-6 pt-2">
      <template v-if="showPayrollTable">
        <div v-if="isLoadingPayroll" class="py-12 flex justify-center items-center">
          <div class="h-8 w-8 border-2 border-[#0d4169]/30 border-t-transparent rounded-full animate-spin"></div>
        </div>

        <div v-else class="w-full">
          <p class="text-[0.78rem] font-semibold text-[#1e293b] mb-3 m-0">
            {{ payrollPeriodTitle }}
          </p>

          <div class="payroll-table-scroll relative overflow-auto min-h-[220px] max-h-[calc(100vh-260px)]">
            <table class="payroll-table w-full min-w-[1200px] border-collapse">
              <thead>
                <tr>
                  <th class="payroll-col-no">No.</th>
                  <th class="payroll-col-employee payroll-sticky-col payroll-sticky-employee">Employee</th>
                  <th class="num">Basic Salary</th>
                  <th class="num">Working Days</th>
                  <th class="num">Monthly Salary</th>
                  <th class="num col-overt">Over Time</th>
                  <th class="num">Total OT</th>
                  <th class="num col-flyout">Earn</th>
                  <th class="num">Total Earn</th>
                  <th class="num col-flyout">Deduct</th>
                  <th class="num">Total Deduct</th>
                  <th class="num">Gross Salary</th>
                  <th class="num">Income Tax</th>
                  <th class="num">{{ pensionEmployeeHeader }}</th>
                  <th class="num">{{ pensionCompanyHeader }}</th>
                  <th class="num">Other Deduct</th>
                  <th class="num">Net Pay</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in sortedPayrollRows"
                  :key="row.userId || row.no"
                  class="hover:bg-slate-50 transition-colors border-b"
                >
                  <td class="payroll-col-no">{{ row.no }}</td>
                  <td class="payroll-col-employee payroll-sticky-col payroll-sticky-employee">{{ row.employee }}</td>
                  <td class="num">{{ fmt(row.basicSalary) }}</td>
                  <td class="num">{{ displayWorkingDays(row.workingDays) }}</td>
                  <td class="num">{{ fmt(row.monthlySalary) }}</td>
                  <td class="num col-overt">
                    <button
                      type="button"
                      class="flyout-action-btn"
                      @click.stop="openFlyout($event, row.userId, 'ot')"
                    >
                      {{ flyoutButtonLabel(row.overtimeEntries) }} ▾
                    </button>
                  </td>
                  <td class="num">{{ fmt(row.totalOt) }}</td>
                  <td class="num col-flyout">
                    <button
                      type="button"
                      class="flyout-action-btn"
                      @click.stop="openFlyout($event, row.userId, 'earn')"
                    >
                      {{ flyoutButtonLabel(row.earnEntries) }} ▾
                    </button>
                  </td>
                  <td class="num">{{ fmt(row.totalEarn) }}</td>
                  <td class="num col-flyout">
                    <button
                      type="button"
                      class="flyout-action-btn"
                      @click.stop="openFlyout($event, row.userId, 'deduct')"
                    >
                      {{ flyoutButtonLabel(row.deductEntries) }} ▾
                    </button>
                  </td>
                  <td class="num">{{ fmt(row.totalConfigDeduct) }}</td>
                  <td class="num payroll-highlight">{{ fmt(row.grossSalary) }}</td>
                  <td class="num">{{ fmt(row.incomeTax) }}</td>
                  <td class="num">{{ fmt(row.pen7) }}</td>
                  <td class="num">{{ fmt(row.pen11) }}</td>
                  <td class="num">{{ fmt(row.otherDeduct) }}</td>
                  <td class="num payroll-highlight">{{ fmt(row.netPay) }}</td>
                </tr>
                <tr v-if="payrollRows.length === 0">
                  <td colspan="17" class="text-center text-slate-400 italic py-5">
                    No saved payroll found for the selected period.
                  </td>
                </tr>
              </tbody>
              <tfoot v-if="payrollRows.length > 0">
                <tr class="font-semibold bg-slate-100 payroll-tfoot-row">
                  <td class="payroll-col-no">Total</td>
                  <td class="payroll-col-employee payroll-sticky-col payroll-sticky-employee"></td>
                  <td class="num">{{ fmt(totals.basicSalary) }}</td>
                  <td class="num">{{ totals.workingDays }}</td>
                  <td class="num">{{ fmt(totals.monthlySalary) }}</td>
                  <td class="num"></td>
                  <td class="num">{{ fmt(totals.totalOt) }}</td>
                  <td class="num"></td>
                  <td class="num">{{ fmt(totals.totalEarn) }}</td>
                  <td class="num"></td>
                  <td class="num">{{ fmt(totals.totalConfigDeduct) }}</td>
                  <td class="num payroll-highlight">{{ fmt(totals.grossSalary) }}</td>
                  <td class="num">{{ fmt(totals.incomeTax) }}</td>
                  <td class="num">{{ fmt(totals.pen7) }}</td>
                  <td class="num">{{ fmt(totals.pen11) }}</td>
                  <td class="num">{{ fmt(totals.otherDeduct) }}</td>
                  <td class="num payroll-highlight">{{ fmt(totals.netPay) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </template>

      <p
        v-else
        class="text-sm text-slate-500 text-center py-10 m-0 italic"
      >
        Select a month or date range to view saved payroll.
      </p>
    </div>

    <Teleport to="body">
      <div
        v-if="flyoutPanel.rowNo !== null"
        ref="flyoutPanelEl"
        class="payroll-flyout fixed z-[9999] bg-white border border-[#cbd5e1] rounded-md shadow-xl text-left"
        :class="{
          'payroll-flyout--list-250': flyoutPanel.mode === 'ot' || flyoutPanel.mode === 'earn' || flyoutPanel.mode === 'deduct',
          'payroll-flyout--no-truncate': flyoutPanel.mode === 'ot' || flyoutPanel.mode === 'earn'
        }"
        :style="{ top: flyoutPanel.top + 'px', left: flyoutPanel.left + 'px', maxHeight: flyoutPanel.maxH + 'px' }"
        @click.stop
      >
        <div class="px-3 py-2 text-[11px] font-bold text-[#0d4169] border-b border-slate-100 whitespace-nowrap">
          {{ flyoutPanelTitle }}
        </div>
        <div class="overflow-y-auto pb-10 payroll-flyout-body">
          <template v-if="flyoutPanel.mode === 'ot'">
            <div
              v-for="ot in panelOvertimeEntries"
              :key="ot.ot_id"
              class="px-3 py-2 border-b border-slate-50 hover:bg-slate-50"
            >
              <div class="payroll-flyout-row payroll-flyout-row--aligned">
                <span class="payroll-flyout-label text-xs font-medium text-slate-700">
                  {{ otEntryLabel(ot) }}
                </span>
                <span class="payroll-flyout-metric text-xs text-slate-500">
                  {{ displayOtHours(ot.hrs) }} hrs
                </span>
                <span class="payroll-flyout-amount text-xs font-semibold text-[#0d4169]">
                  {{ fmt(overtimePayForEntry(panelRow, ot)) }}
                </span>
              </div>
            </div>
            <p v-if="panelOvertimeEntries.length === 0" class="text-xs text-slate-400 px-3 py-4 text-center m-0">
              No overtime saved for this employee.
            </p>
          </template>
          <template v-else-if="flyoutPanel.mode === 'earn'">
            <div
              v-for="entry in panelEarnEntries"
              :key="entry.id || `${entry.definitionId}-${entry.amount}`"
              class="px-3 py-2 border-b border-slate-50 hover:bg-slate-50"
            >
              <div class="payroll-flyout-row payroll-flyout-row--aligned">
                <span class="payroll-flyout-label text-xs font-medium text-slate-700">
                  {{ entry.definitionName || '—' }}
                </span>
                <span class="payroll-flyout-metric text-xs text-slate-500">
                  {{ formatConfigAmountLabel(entry) }}
                </span>
                <span class="payroll-flyout-amount text-xs font-semibold text-[#0d4169]">
                  {{ fmt(configEntryPayAmount(panelRow, entry)) }}
                </span>
              </div>
            </div>
            <p v-if="panelEarnEntries.length === 0" class="text-xs text-slate-400 px-3 py-4 text-center m-0">
              No earn entries saved for this employee.
            </p>
          </template>
          <template v-else-if="flyoutPanel.mode === 'deduct'">
            <div
              v-for="entry in panelDeductEntries"
              :key="entry.id || `${entry.definitionId}-${entry.amount}`"
              class="px-3 py-2 border-b border-slate-50 hover:bg-slate-50"
            >
              <div class="payroll-flyout-row payroll-flyout-row--aligned">
                <span class="payroll-flyout-label text-xs font-medium text-slate-700" :title="entry.definitionName">
                  {{ entry.definitionName || '—' }}
                </span>
                <span class="payroll-flyout-metric text-xs text-slate-500">
                  {{ formatConfigAmountLabel(entry) }}
                </span>
                <span class="payroll-flyout-amount text-xs font-semibold text-[#0d4169]">
                  {{ fmt(configEntryPayAmount(panelRow, entry)) }}
                </span>
              </div>
            </div>
            <p v-if="panelDeductEntries.length === 0" class="text-xs text-slate-400 px-3 py-4 text-center m-0">
              No deduct entries saved for this employee.
            </p>
          </template>
        </div>
        <button
          type="button"
          class="absolute bottom-2 right-2 text-[11px] font-bold text-[#2563eb] underline"
          @click="closeFlyout"
        >
          Close
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, defineProps, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import FinanceToast from '../FinanceToast.vue'
import MonthlyConfigurationFilters from '../monthlyConfiguration/monthlyConfigurationFilters.vue'
import { useMonthlyConfigurationFilters } from '../monthlyConfiguration/useMonthlyConfigurationFilters.js'
import { apiFetch } from '../../../utils/api'
import { ALL_BRANCHES_SCOPE_VALUE } from '../monthlyConfiguration/useMonthlyConfigurationBranches.js'
import { MAIN_COMPANY_SCOPE_VALUE } from '../monthlyConfiguration/monthlyConfigurationScope.js'
import {
  getGregorianRangeForSelectedMonth,
  getMonthOptionByValue
} from '../../../utils/ethiopianMonth'

const props = defineProps({
  apiBase: {
    type: String,
    default: '/finance'
  },
  payrollPath: {
    type: String,
    default: ''
  },
  skipPrivilegeCheck: {
    type: Boolean,
    default: false
  }
})

const store = useStore()
const payrollApiPath = computed(() => props.payrollPath || `${props.apiBase}/payroll`)
const pensionApiPath = computed(() => `${props.apiBase}/pension`)
const overtimeApiPath = computed(() => `${props.apiBase}/overtime`)

const resolvedCompanyName = computed(() => {
  const user = store.getters.currentUser || store.state.user || {}
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})

const { form, isSearchingUsers } = useMonthlyConfigurationFilters()

const toast = reactive({
  show: false,
  message: '',
  variant: 'success'
})
let toastTimer = null

function showToast(message, variant = 'success') {
  if (toastTimer) {
    window.clearTimeout(toastTimer)
    toastTimer = null
  }
  toast.message = message
  toast.variant = variant
  toast.show = true
  const duration = variant === 'error' ? 5000 : 3000
  toastTimer = window.setTimeout(() => {
    toast.show = false
    toast.variant = 'success'
    toastTimer = null
  }, duration)
}

const pensionRates = ref({
  employeeContribution: 0,
  companyContribution: 0
})
const overtimeNameById = ref(new Map())
const isLoadingPayroll = ref(false)
const payrollRows = ref([])
let payrollRequestId = 0

const sortedPayrollRows = computed(() => {
  return [...payrollRows.value]
    .sort((a, b) =>
      String(a?.employee || '').localeCompare(String(b?.employee || ''), undefined, { sensitivity: 'base' })
    )
    .map((row, idx) => ({ ...row, no: idx + 1 }))
})

const flyoutPanelEl = ref(null)
const flyoutPanel = reactive({
  mode: null,
  rowNo: null,
  top: 0,
  left: 0,
  maxH: 400
})

const MONTH_LABELS = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December'
}

function englishMonthLabel(monthValue) {
  const option = getMonthOptionByValue(monthValue)
  if (option?.label) return option.label
  const mapped = MONTH_LABELS[Number(monthValue)] || MONTH_LABELS[monthValue]
  if (mapped) return mapped
  return String(monthValue || '')
    .split('|')[0]
    .trim()
}

function formatDisplayDate(isoDate) {
  if (!isoDate) return ''
  const d = new Date(`${isoDate}T00:00:00`)
  if (Number.isNaN(d.getTime())) return isoDate
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

const payrollPeriodTitle = computed(() => {
  if (form.fromDate && form.toDate) {
    return `Saved payroll for ${formatDisplayDate(form.fromDate)} - ${formatDisplayDate(form.toDate)}`
  }
  if (form.fromDate || form.toDate) {
    const from = form.fromDate ? formatDisplayDate(form.fromDate) : '—'
    const to = form.toDate ? formatDisplayDate(form.toDate) : '—'
    return `Saved payroll for ${from} - ${to}`
  }
  if (form.selectedMonth) {
    const range = getGregorianRangeForSelectedMonth(form.selectedMonth)
    const monthLabel = englishMonthLabel(form.selectedMonth)
    if (range?.fromDisplay && range?.toDisplay) {
      return `Saved payroll for the month of ${monthLabel}  ${range.fromDisplay} to ${range.toDisplay}`
    }
    return `Saved payroll for the month of ${monthLabel}`
  }
  return 'Saved payroll for the month of _______'
})

const showPayrollTable = computed(() => {
  if (form.selectedMonth) return true
  if (form.fromDate && form.toDate) return true
  return false
})

const panelRow = computed(() => payrollRows.value.find((r) => r.userId === flyoutPanel.rowNo) || null)
const panelOvertimeEntries = computed(() => {
  const entries = panelRow.value?.overtimeEntries
  return Array.isArray(entries) ? entries : []
})
const panelEarnEntries = computed(() => {
  const entries = panelRow.value?.earnEntries
  return Array.isArray(entries) ? entries : []
})
const panelDeductEntries = computed(() => {
  const entries = panelRow.value?.deductEntries
  return Array.isArray(entries) ? entries : []
})

const flyoutPanelTitle = computed(() => {
  if (flyoutPanel.mode === 'ot') return 'Overtime (saved)'
  if (flyoutPanel.mode === 'earn') return 'Earn (saved)'
  if (flyoutPanel.mode === 'deduct') return 'Deduct (saved)'
  return ''
})

function parsePensionPercent(value) {
  if (value === null || value === undefined || value === '') return 0
  const num = Number(String(value).replace(/%/g, '').trim())
  return Number.isFinite(num) && num >= 0 ? num : 0
}

function formatPensionPctLabel(value) {
  const pct = parsePensionPercent(value)
  if (pct <= 0) return ''
  return String(pct)
}

const pensionEmployeeHeader = computed(() => {
  const label = formatPensionPctLabel(pensionRates.value.employeeContribution)
  return label ? `Pen. ${label}%` : 'Pen. (Employee)'
})

const pensionCompanyHeader = computed(() => {
  const label = formatPensionPctLabel(pensionRates.value.companyContribution)
  return label ? `Pen. ${label}%` : 'Pen. (Company)'
})

function normalizedEntityForFetch() {
  const scope = String(form.selectedBranchId || '').trim()
  if (scope === String(ALL_BRANCHES_SCOPE_VALUE)) return 'all'
  return String(form.selectEntity || '').trim()
}

function hasValidPeriod() {
  return Boolean(form.selectedMonth || (form.fromDate && form.toDate))
}

function canFetchSavedPayroll() {
  if (!hasValidPeriod()) return false
  const scope = String(form.selectedBranchId || '').trim()
  if (!scope) return false
  if (scope === String(ALL_BRANCHES_SCOPE_VALUE)) return true

  const entity = normalizedEntityForFetch()
  if (!entity) return false
  if (entity === 'by_department') return Boolean(String(form.departmentId || '').trim())
  if (entity === 'by_name') return Boolean(String(form.searchName || '').trim()) && !isSearchingUsers.value
  return entity === 'all'
}

function buildPayrollPeriodQueryParams(includeDepartment = false) {
  const params = new URLSearchParams()
  if (form.selectedMonth) {
    params.set('month', String(form.selectedMonth))
  } else if (form.fromDate && form.toDate) {
    params.set('fromDate', form.fromDate)
    params.set('toDate', form.toDate)
  } else {
    return null
  }
  if (includeDepartment && normalizedEntityForFetch() === 'by_department') {
    const depId = String(form.departmentId || '').trim()
    if (depId) params.set('departmentId', depId)
  }
  return params
}

function matchesBranchScope(savedRow) {
  const scope = String(form.selectedBranchId || '').trim()
  if (!scope || scope === String(ALL_BRANCHES_SCOPE_VALUE)) return true
  if (scope === String(MAIN_COMPANY_SCOPE_VALUE)) {
    return savedRow.branchId === null || savedRow.branchId === undefined
  }
  const branchId = Number(scope)
  if (!Number.isFinite(branchId)) return true
  const rowBranchId =
    savedRow.branchId === null || savedRow.branchId === undefined ? null : Number(savedRow.branchId)
  return rowBranchId === branchId
}

function buildPensionFetchUrl() {
  const scope = String(form.selectedBranchId || '').trim()
  if (
    !scope ||
    scope === String(ALL_BRANCHES_SCOPE_VALUE) ||
    scope === String(MAIN_COMPANY_SCOPE_VALUE)
  ) {
    return pensionApiPath.value
  }
  const branchId = Number(scope)
  if (Number.isFinite(branchId) && branchId > 0) {
    return `${pensionApiPath.value}?branchId=${branchId}`
  }
  return pensionApiPath.value
}

async function loadPensionRates() {
  try {
    const response = await apiFetch(buildPensionFetchUrl(), { method: 'GET' })
    if (!response.ok) {
      pensionRates.value = { employeeContribution: 0, companyContribution: 0 }
      return
    }
    const data = await response.json().catch(() => null)
    if (!data || typeof data !== 'object') {
      pensionRates.value = { employeeContribution: 0, companyContribution: 0 }
      return
    }
    pensionRates.value = {
      employeeContribution: parsePensionPercent(data.employeeContribution),
      companyContribution: parsePensionPercent(data.companyContribution)
    }
  } catch {
    pensionRates.value = { employeeContribution: 0, companyContribution: 0 }
  }
}

async function fetchPayrollUserNameMap() {
  const scope = String(form.selectedBranchId || '').trim()
  if (!scope) return new Map()

  const params = new URLSearchParams()
  params.set('scope', scope)
  params.set('entity', normalizedEntityForFetch() || 'all')
  if (form.departmentId) params.set('departmentId', String(form.departmentId))
  if (String(form.searchName || '').trim()) params.set('q', String(form.searchName || '').trim())

  try {
    const response = await apiFetch(`/users/monthly-configuration/payroll-users?${params.toString()}`, {
      method: 'GET'
    })
    if (!response.ok) return new Map()
    const users = await response.json().catch(() => [])
    const map = new Map()
    for (const user of Array.isArray(users) ? users : []) {
      const uid = Number(user?.id)
      if (!Number.isFinite(uid)) continue
      map.set(uid, user)
    }
    return map
  } catch {
    return new Map()
  }
}

function normalizeAttendanceInt(value) {
  if (value === null || value === undefined || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? Math.trunc(num) : null
}

function parseAttendanceOvertimeEntries(raw) {
  if (!raw) return []
  const list = Array.isArray(raw) ? raw : []
  return list
    .map((entry) => {
      const otId = normalizeAttendanceInt(entry?.ot_id ?? entry?.otId)
      const hrs = Number(entry?.hrs ?? entry?.hours ?? 0)
      const percentRaw = entry?.percent ?? entry?.otRate ?? entry?.ot_rate
      const percent =
        percentRaw !== null && percentRaw !== undefined && percentRaw !== ''
          ? Number(percentRaw)
          : null
      return {
        ot_id: otId,
        hrs,
        percent: Number.isFinite(percent) ? percent : null
      }
    })
    .filter((entry) => entry.ot_id != null && Number.isFinite(entry.hrs) && entry.hrs > 0)
    .sort((a, b) => a.ot_id - b.ot_id)
}

function normalizeConfigEntry(raw) {
  return {
    id: raw?.id != null ? Number(raw.id) : null,
    definitionId: raw?.definitionId != null ? Number(raw.definitionId) : null,
    definitionName: String(raw?.definitionName || '').trim(),
    amountType: String(raw?.amountType || '').trim(),
    amount: String(raw?.amount ?? '').trim(),
    applicableFor: String(raw?.applicableFor || '').trim()
  }
}

function deriveDueDateFromSaved(basicSalary, workingDays, monthlySalary) {
  const basic = n(basicSalary)
  const days = n(workingDays)
  const monthly = n(monthlySalary)
  if (basic <= 0 || days <= 0 || monthly <= 0) return null
  const due = (basic * days) / monthly
  return Number.isFinite(due) && due > 0 ? due : null
}

async function loadOvertimeNameMap() {
  try {
    const response = await apiFetch(overtimeApiPath.value, { method: 'GET' })
    if (!response.ok) {
      overtimeNameById.value = new Map()
      return
    }
    const rows = await response.json().catch(() => [])
    const map = new Map()
    for (const row of Array.isArray(rows) ? rows : []) {
      const id = String(row?.id ?? '').trim()
      if (!id) continue
      const name = String(row?.otName || '').trim()
      map.set(id, name || `OT ${id}`)
    }
    overtimeNameById.value = map
  } catch {
    overtimeNameById.value = new Map()
  }
}

function mapSavedRowToDisplay(saved, idx, nameMap) {
  const userId = Number(saved.userId)
  const user = nameMap.get(userId)
  const employee = String(user?.fullName || user?.username || `User ${userId}`)
  const basicSalary = n(saved.basicSalary)
  const workingDays = saved.workingDays ?? null
  const monthlySalary = n(saved.monthlySalary)

  return {
    no: idx + 1,
    userId,
    branchId: saved.branchId ?? null,
    employee,
    basicSalary,
    workingDays,
    monthlySalary,
    dueDate: deriveDueDateFromSaved(basicSalary, workingDays, monthlySalary),
    overtimeEntries: parseAttendanceOvertimeEntries(saved.overtimeEntries ?? saved.overtime_entries),
    totalOt: n(saved.totalOt),
    earnEntries: Array.isArray(saved.earnEntries ?? saved.earn_entries)
      ? (saved.earnEntries ?? saved.earn_entries).map(normalizeConfigEntry)
      : [],
    totalEarn: n(saved.totalEarn),
    deductEntries: Array.isArray(saved.deductEntries ?? saved.deduct_entries)
      ? (saved.deductEntries ?? saved.deduct_entries).map(normalizeConfigEntry)
      : [],
    totalConfigDeduct: n(saved.totalConfigDeduct),
    grossSalary: n(saved.grossSalary),
    incomeTax: n(saved.incomeTax),
    pen7: n(saved.pen7),
    pen11: n(saved.pen11),
    otherDeduct: n(saved.otherDeduct),
    netPay: n(saved.netPay)
  }
}

async function loadSavedPayrollReport() {
  if (!canFetchSavedPayroll()) {
    payrollRows.value = []
    closeFlyout()
    return
  }

  const requestId = ++payrollRequestId
  isLoadingPayroll.value = true

  try {
    const params = buildPayrollPeriodQueryParams(true)
    if (!params) {
      payrollRows.value = []
      return
    }

    const entity = normalizedEntityForFetch()
    if (entity === 'by_name') {
      const nameMapForFilter = await fetchPayrollUserNameMap()
      if (requestId !== payrollRequestId) return
      const userIds = [...nameMapForFilter.keys()]
      if (!userIds.length) {
        payrollRows.value = []
        return
      }
      params.set('userIds', userIds.join(','))
    }

    const response = await apiFetch(`${payrollApiPath.value}?${params.toString()}`, { method: 'GET' })
    if (requestId !== payrollRequestId) return

    if (!response.ok) {
      payrollRows.value = []
      const data = await response.json().catch(() => ({}))
      showToast(data?.message || 'Failed to load saved payroll.', 'error')
      return
    }

    const savedRows = await response.json().catch(() => [])
    if (!Array.isArray(savedRows)) {
      payrollRows.value = []
      return
    }

    const filteredRows = savedRows.filter(matchesBranchScope)
    const nameMap = await fetchPayrollUserNameMap()
    if (requestId !== payrollRequestId) return

    payrollRows.value = filteredRows.map((saved, idx) => mapSavedRowToDisplay(saved, idx, nameMap))
  } catch {
    if (requestId !== payrollRequestId) return
    payrollRows.value = []
    showToast('Failed to load saved payroll.', 'error')
  } finally {
    if (requestId === payrollRequestId) {
      isLoadingPayroll.value = false
    }
  }
}

function n(v) {
  const x = Number(v)
  return Number.isFinite(x) ? x : 0
}

function fmt(value) {
  if (value === null || value === undefined || value === '') return ''
  const num = Number(value)
  if (Number.isNaN(num)) return ''
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function displayWorkingDays(value) {
  if (value === null || value === undefined || value === '') return ''
  const num = Number(value)
  if (!Number.isFinite(num)) return ''
  return String(Math.trunc(num))
}

function flyoutButtonLabel(entries) {
  const count = Array.isArray(entries) ? entries.length : 0
  return count > 0 ? 'View' : 'List'
}

function overtimeRateForEntry(row, entry) {
  const basic = n(row?.basicSalary)
  const due = n(row?.dueDate)
  const percent = entry?.percent != null && entry?.percent !== '' ? Number(entry.percent) : 0
  const pct = Number.isFinite(percent) ? percent : 0
  if (basic <= 0 || due <= 0) return 0
  return (basic / (due * 8)) * (1 + pct / 100)
}

function overtimePayForEntry(row, entry) {
  const hrs = n(entry?.hrs)
  if (hrs <= 0) return 0
  return hrs * overtimeRateForEntry(row, entry)
}

function otEntryLabel(entry) {
  const id = entry?.ot_id != null ? String(entry.ot_id) : ''
  const name = id ? overtimeNameById.value.get(id) || `OT ${id}` : 'OT'
  const percent = entry?.percent
  if (percent != null && Number.isFinite(Number(percent))) {
    return `${name} (${Number(percent)}%)`
  }
  return name
}

function displayOtHours(value) {
  if (value === null || value === undefined || value === '') return '—'
  const num = Number(value)
  if (!Number.isFinite(num)) return '—'
  if (Number.isInteger(num)) return String(num)
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatConfigAmountLabel(entry) {
  const amount = String(entry?.amount ?? '').trim()
  if (!amount) return '—'
  const type = String(entry?.amountType || '').toLowerCase()
  if (type === 'salary_percent') return `${amount} (salary %)`
  if (type === 'fixed') return `${amount} (fixed)`
  return amount
}

function configEntryPayAmount(row, entry) {
  const amount = Number(entry?.amount)
  if (!Number.isFinite(amount)) return 0
  const type = String(entry?.amountType || '').toLowerCase()
  if (type === 'salary_percent') {
    return (n(row?.monthlySalary) * amount) / 100
  }
  if (type === 'fixed') {
    return amount
  }
  return 0
}

function sumField(field) {
  return payrollRows.value.reduce((acc, row) => {
    const v = row[field]
    if (v === null || v === undefined || v === '') return acc
    const num = Number(v)
    return Number.isNaN(num) ? acc : acc + num
  }, 0)
}

const totals = computed(() => ({
  basicSalary: sumField('basicSalary'),
  workingDays: sumField('workingDays'),
  monthlySalary: sumField('monthlySalary'),
  totalOt: sumField('totalOt'),
  totalEarn: sumField('totalEarn'),
  totalConfigDeduct: sumField('totalConfigDeduct'),
  grossSalary: sumField('grossSalary'),
  incomeTax: sumField('incomeTax'),
  pen7: sumField('pen7'),
  pen11: sumField('pen11'),
  otherDeduct: sumField('otherDeduct'),
  netPay: sumField('netPay')
}))

function positionFlyoutWithinViewport(anchorRect) {
  const el = flyoutPanelEl.value
  if (!el) return

  const pad = 8
  const scrollY = window.scrollY || window.pageYOffset
  const scrollX = window.scrollX || window.pageXOffset
  const panelW = el.offsetWidth
  const viewportLeft = scrollX + pad
  const viewportRight = scrollX + window.innerWidth - pad

  let left = anchorRect.left + scrollX
  if (left + panelW > viewportRight) {
    left = Math.max(viewportLeft, viewportRight - panelW)
  }
  if (left < viewportLeft) {
    left = viewportLeft
  }
  flyoutPanel.left = left

  const panelH = el.offsetHeight
  const topBelow = anchorRect.bottom + scrollY + 6
  const maxTop = scrollY + window.innerHeight - panelH - pad
  flyoutPanel.top = Math.max(scrollY + pad, Math.min(topBelow, maxTop))
}

function openFlyout(evt, rowNo, mode) {
  const rect = evt.currentTarget.getBoundingClientRect()
  const scrollY = window.scrollY || window.pageYOffset
  const scrollX = window.scrollX || window.pageXOffset
  flyoutPanel.mode = mode
  flyoutPanel.rowNo = rowNo
  const topBelow = rect.bottom + scrollY + 6
  const leftAnchored = rect.left + scrollX
  const maxH = window.innerHeight - (rect.bottom - window.scrollY + 80)
  flyoutPanel.maxH = Math.max(240, Math.min(440, maxH))
  flyoutPanel.top = Math.max(scrollY + 8, Math.min(topBelow, scrollY + window.innerHeight - 120))
  flyoutPanel.left = leftAnchored
  nextTick(() => {
    positionFlyoutWithinViewport(rect)
    bindOutsideClose()
  })
}

function closeFlyout() {
  flyoutPanel.mode = null
  flyoutPanel.rowNo = null
  removeOutsideClose()
}

let outsideHandler = null

function bindOutsideClose() {
  removeOutsideClose()
  outsideHandler = (e) => {
    const el = flyoutPanelEl.value
    if (el && el.contains(e.target)) return
    closeFlyout()
  }
  document.addEventListener('pointerdown', outsideHandler)
}

function removeOutsideClose() {
  if (outsideHandler) {
    document.removeEventListener('pointerdown', outsideHandler)
    outsideHandler = null
  }
}

watch(
  () => form.selectedBranchId,
  () => {
    loadPensionRates()
  }
)

watch(
  () => [
    form.selectedMonth,
    form.fromDate,
    form.toDate,
    form.selectedBranchId,
    form.selectEntity,
    form.departmentId,
    form.searchName,
    isSearchingUsers.value
  ],
  () => {
    loadSavedPayrollReport()
  }
)

onMounted(async () => {
  await Promise.all([loadPensionRates(), loadOvertimeNameMap()])
  loadSavedPayrollReport()
})

onBeforeUnmount(() => {
  removeOutsideClose()
})
</script>

<style scoped>
.payroll-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: separate;
  border-spacing: 0;
}

.payroll-table th,
.payroll-table td {
  padding: 4px 10px;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
  font-size: 0.72rem;
  line-height: 1.2;
  white-space: nowrap;
}

.payroll-table thead th {
  background: #f1f5f9;
  font-weight: 600;
  color: #1e293b;
  text-align: center;
  position: sticky;
  top: 0;
  z-index: 20;
}

.payroll-col-no {
  width: 2.75rem;
  min-width: 2.75rem;
  max-width: 2.75rem;
}

.payroll-col-employee {
  width: max-content;
  min-width: 6rem;
  max-width: none;
  white-space: nowrap;
}

.payroll-sticky-col {
  position: sticky;
  background: #ffffff;
  z-index: 10;
}

.payroll-sticky-employee {
  left: 0;
}

.payroll-table thead .payroll-sticky-col {
  z-index: 30;
  background: #f1f5f9;
}

.payroll-table tfoot .payroll-sticky-col {
  background: #f1f5f9;
  z-index: 10;
}

.payroll-table tbody tr:hover .payroll-sticky-col {
  background: #f8fafc;
}

.payroll-table tbody td.payroll-col-no,
.payroll-table thead th.payroll-col-no,
.payroll-table tfoot td.payroll-col-no {
  text-align: center;
}

.payroll-table tbody td {
  text-align: left;
}

.payroll-table tbody tr:hover {
  background: #f8fafc;
}

.payroll-table tfoot td {
  background: #f1f5f9;
  border-top: 2px solid #cbd5e1;
  font-weight: 600;
}

.payroll-table tbody td.payroll-col-employee,
.payroll-table thead th.payroll-col-employee,
.payroll-table tfoot td.payroll-col-employee {
  text-align: left;
}

.payroll-table th.num,
.payroll-table td.num {
  width: 5.5rem;
  min-width: 5.5rem;
  text-align: center;
}

.payroll-table th.col-overt,
.payroll-table td.col-overt,
.payroll-table th.col-flyout,
.payroll-table td.col-flyout {
  width: 6.5rem;
  min-width: 6.5rem;
}

.payroll-flyout {
  width: max-content;
  max-width: calc(100vw - 1rem);
  min-width: 0;
}

.payroll-flyout--list-250 {
  width: 250px;
  min-width: 250px;
  max-width: min(250px, calc(100vw - 1rem));
  box-sizing: border-box;
}

.payroll-flyout--list-250:not(.payroll-flyout--no-truncate) .payroll-flyout-body {
  overflow-x: hidden;
}

.payroll-flyout--list-250.payroll-flyout--no-truncate .payroll-flyout-body {
  overflow-x: visible;
}

.payroll-flyout--list-250 > div:first-child {
  white-space: normal;
}

.payroll-flyout-body {
  max-height: 16rem;
  overflow-y: auto;
  overflow-x: auto;
  max-width: 100%;
}

.payroll-flyout-row--aligned {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  column-gap: 0.5rem;
  width: 100%;
  box-sizing: border-box;
}

.payroll-flyout--no-truncate .payroll-flyout-row--aligned {
  align-items: start;
}

.payroll-flyout--list-250:not(.payroll-flyout--no-truncate) .payroll-flyout-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.payroll-flyout--no-truncate .payroll-flyout-label {
  min-width: 0;
  overflow: visible;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.payroll-flyout-metric,
.payroll-flyout-amount {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.payroll-flyout--no-truncate .payroll-flyout-metric {
  white-space: normal;
  overflow-wrap: anywhere;
}

.flyout-action-btn {
  width: 100%;
  padding: 2px 6px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  font-size: 0.68rem;
  font-weight: 600;
  color: #0d4169;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;
}

.flyout-action-btn:hover {
  border-color: #0d4169;
}

.payroll-table td.payroll-highlight {
  font-weight: 700;
  color: #475569;
}
</style>
